import { z } from "zod";

const hasAnyValue = (values: (string | null | undefined)[]): boolean => {
  return values.some((v) => v && v.trim().length > 0);
};

export const insuranceInfoValidationSchema = z
  .object({
    insuranceProvider: z.string().nullable().optional(),
    memberId: z.string().nullable().optional(),
    groupNumber: z.string().nullable().optional(),
    planCategory: z.string().nullable().optional(),
    denyingParty: z.string().nullable().optional(),
    relationshipToPolicyholder: z
      .union([z.enum(["SELF", "SPOUSE", "DEPENDENT"]), z.literal("")])
      .nullable()
      .optional(),
    policyholderFirstName: z.string().nullable().optional(),
    policyholderLastName: z.string().nullable().optional(),
    employerName: z.string().nullable().optional(),
    pharmacyBenefitManager: z.string().nullable().optional(),
    rxBin: z.string().nullable().optional(),
    rxGroup: z.string().nullable().optional(),
    rxMemberId: z.string().nullable().optional(),
    otherDenyingPartyName: z.string().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    const insuranceFields = [
      data.insuranceProvider,
      data.memberId,
      data.planCategory,
    ];

    const insuranceGroupHasData = hasAnyValue(insuranceFields);

    if (insuranceGroupHasData) {
      const missingFields = [];

      if (
        !data.insuranceProvider ||
        data.insuranceProvider.trim().length === 0
      ) {
        missingFields.push("insuranceProvider");
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Insurance provider is required",
          path: ["insuranceProvider"],
        });
      }

      if (!data.memberId || data.memberId.trim().length === 0) {
        missingFields.push("memberId");
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Member ID is required",
          path: ["memberId"],
        });
      }

      if (!data.planCategory || data.planCategory.trim().length === 0) {
        missingFields.push("planCategory");
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Plan type is required",
          path: ["planCategory"],
        });
      }

      if (
        data.relationshipToPolicyholder &&
        !["SELF", "SPOUSE", "DEPENDENT"].includes(
          data.relationshipToPolicyholder,
        )
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Relationship to policyholder must be SELF, SPOUSE, or DEPENDENT",
          path: ["relationshipToPolicyholder"],
        });
      }

      if (
        data.relationshipToPolicyholder &&
        data.relationshipToPolicyholder !== "SELF"
      ) {
        if (
          !data.policyholderFirstName ||
          data.policyholderFirstName.trim().length === 0
        ) {
          missingFields.push("policyholderFirstName");
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "When relationship to policyholder is not SELF, policyholder first name is required",
            path: ["policyholderFirstName"],
          });
        }
        if (
          !data.policyholderLastName ||
          data.policyholderLastName.trim().length === 0
        ) {
          missingFields.push("policyholderLastName");
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "When relationship to policyholder is not SELF, policyholder last name is required",
            path: ["policyholderLastName"],
          });
        }
      }

      if (missingFields.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please complete all required insurance fields",
          path: ["insuranceGroup"],
        });
      }
    }

    const denialFields = [data.denyingParty, data.otherDenyingPartyName];

    const denialGroupHasData = hasAnyValue(denialFields);

    if (denialGroupHasData) {
      const missingDenialFields = [];

      if (!data.denyingParty || data.denyingParty.trim().length === 0) {
        missingDenialFields.push("denyingParty");
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Denying party is required",
          path: ["denyingParty"],
        });
      } else if (data.denyingParty === "OTHER") {
        if (
          !data.otherDenyingPartyName ||
          data.otherDenyingPartyName.trim().length === 0
        ) {
          missingDenialFields.push("otherDenyingPartyName");
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "When denying party is OTHER, other denying party name is required",
            path: ["otherDenyingPartyName"],
          });
        }
      }

      if (missingDenialFields.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please complete all required denial fields",
          path: ["denialGroup"],
        });
      }
    }
  });

export type InsuranceInfoValidationSchema = z.infer<
  typeof insuranceInfoValidationSchema
>;
